import React from 'react'
import { Box, Stack, Typography, useMediaQuery, useTheme } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { IProjectInfo } from 'src/generated-types/project'
import { IProjectStatus } from 'src/generated-types/project-status'
import { AppRoutesNavigation } from 'src/app-routes'
import { projectStatusColors } from 'src/constant'
import { MUIThemeColors } from 'src/mui-theme'
import { getThemeColor } from 'src/utils'
import { formatVersionLabel, getStatusChips } from './projectVersion.action'

interface ProjectStatusChipsProps {
  project: IProjectInfo
  variant: 'table' | 'card'
}

export const ProjectStatusChips: React.FC<ProjectStatusChipsProps> = ({ project, variant }) => {
  const { t } = useTranslation('projects')
  const theme = useTheme()
  const navigate = useNavigate()
  const isMobile = useMediaQuery<any>(theme.breakpoints.down('sm'))
  const chips = getStatusChips(project)
  const isVersioned = !!project.versions?.length

  const openVersion = (e: React.MouseEvent, projectId: number) => {
    e.stopPropagation()
    navigate(AppRoutesNavigation.Project.project(project.customerId, project.localityId, projectId))
  }

  const labelOf = (status: IProjectStatus, versionNumber?: number) =>
    `${t(IProjectStatus[status], { ns: 'projects' })}${versionNumber ? ` ${formatVersionLabel(versionNumber)}` : ''}`

  if (variant === 'card') {
    // The card clips its lower half, so stacked chips are pinned to the corner and kept compact.
    const isStacked = chips.length > 1 && !isMobile
    return (
      <Stack
        spacing={'2px'}
        alignItems="flex-end"
        sx={{ width: 'fit-content', ...(isStacked ? { position: 'absolute', bottom: '6px', right: 0 } : {}) }}
      >
        {chips.map(chip => (
          <Typography
            key={chip.projectId}
            onClick={isVersioned ? e => openVersion(e, chip.projectId) : undefined}
            sx={{
              backgroundColor: getThemeColor(theme, projectStatusColors[chip.status]),
              padding: isMobile ? '3px 12px' : isStacked ? '1px 5px' : '3px 5px',
              borderRadius: isMobile ? '4px 0px 0px 4px' : '3px 0px 0px 3px',
              boxShadow: isMobile ? '0px 2px 4px rgba(0,0,0,0.2)' : 'none',
              fontSize: isMobile ? '10px' : isStacked ? '11px' : '12px',
              lineHeight: isStacked ? '16px' : undefined,
              fontWeight: isMobile ? 600 : 300,
              cursor: isVersioned ? 'pointer' : 'inherit',
            }}
            color={getThemeColor(theme, MUIThemeColors.white)}
            variant="caption"
          >
            {labelOf(chip.status, chip.versionNumber)}
          </Typography>
        ))}
      </Stack>
    )
  }

  return (
    <Box sx={{ display: 'flex', gap: '4px', flexWrap: 'wrap', py: '4px' }}>
      {chips.map(chip => (
        <Box
          key={chip.projectId}
          onClick={isVersioned ? e => openVersion(e, chip.projectId) : undefined}
          sx={{
            backgroundColor: getThemeColor(theme, projectStatusColors[chip.status]),
            color: getThemeColor(theme, MUIThemeColors.white),
            borderRadius: '4px',
            padding: '4px 8px',
            cursor: isVersioned ? 'pointer' : 'inherit',
          }}
        >
          {labelOf(chip.status, chip.versionNumber)}
        </Box>
      ))}
    </Box>
  )
}

export default ProjectStatusChips
